import { useEffect, useState } from 'react';
import useCustomer from '@hooks/useCustomer';
import useCustomRouter from '@hooks/useCustomRouter';
import paths from '@constants/paths';
import { NextComponentType } from 'next';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import { selectIsLoggingIn } from '@slices/loginSlice';
import useUserAgent from '@hooks/useUserAgent';
import ErrorComponent from '@molecules/ErrorComponent/ErrorComponent';

const WithProtectedRoute = (Component: NextComponentType, currentAsPath: string, from?: string) => {
  const CompWithProtectedRoute = (props: any) => {
    const { customer } = useCustomer();
    const router = useCustomRouter();
    const { isNativeApp } = useUserAgent();
    const isLoggingIn = useAppSelector(selectIsLoggingIn);
    const [hasShownLoginModal, setHasShowLoginModal] = useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
      // Redirect to start page if user aborts login on a password protected page
      if (
        // Needs to check for strict false as the web will init with isNativeApp as undefined
        isNativeApp === false &&
        customer?.isAnonymous &&
        customer &&
        hasShownLoginModal &&
        router.asPath === currentAsPath &&
        !isLoggingIn
      ) {
        const { ...query } = router.query;
        router.push({ pathname: paths.HOME, query }, { pathname: paths.HOME }, { shallow: true });
      }
    }, [isNativeApp, customer?.isAnonymous, customer, hasShownLoginModal, router, isLoggingIn]);

    useEffect(() => {
      // Needs to check for strict false as the web will init with isNativeApp as undefined
      if (isNativeApp === false && customer && customer.isAnonymous) {
        router.push(
          { pathname: router.pathname, query: { ...router.query, from: from || '' } },
          { pathname: paths.USER_LOGIN },
          { shallow: true }
        );
      }
    }, [isNativeApp, customer?.isAnonymous, customer]);

    useEffect(() => {
      // Needs to check for strict false as the web will init with isNativeApp as undefined
      if (isNativeApp === false && !customer?.isAnonymous && customer && router.query?.from) {
        // Remove 'from' from query
        const { from, ...query } = router.query;
        router.replace({ pathname: currentAsPath, query }, undefined);
      }
    }, [isNativeApp, customer?.isAnonymous, customer]);

    useEffect(() => {
      if (router.asPath === paths.USER_LOGIN) {
        setHasShowLoginModal(true);
      }
    }, [router.asPath]);

    if (isNativeApp && customer && customer.isAnonymous) {
      return <ErrorComponent errorCode={401} />;
    }

    return !customer?.isAnonymous && customer ? <Component {...props} /> : null;
  };

  return CompWithProtectedRoute;
};

export default WithProtectedRoute;
